/**
 * Job Tracker Component
 * Excel-like grid for managing job requisitions
 */

import React, { useState, useEffect, useCallback } from 'react';
import jobService from '../shared/services/jobService';
import EditableCell from '../shared/components/EditableCell';
import JobForm from './components/JobForm';
import { JOB_COLUMNS, JOB_STATUS_OPTIONS } from './utils/jobColumns';
import { crmDataService } from '../../HR/CRM/utils/crmDataService';
import './styles/JobTracker.css';

function JobTracker() {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});
  const [stores, setStores] = useState([]);
  const [jobstatusoptions, setJobStatusOptions] = useState(JOB_STATUS_OPTIONS);

  // Load jobs, stores, and statuses on mount
  useEffect(() => {
    loadData();
    loadStores();
    loadStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Excel-like column filtering
  const applyFilters = useCallback(() => {
    let filtered = [...jobs];

    Object.keys(columnFilters).forEach(columnKey => {
      const filterValue = columnFilters[columnKey];
      if (filterValue && filterValue.trim() !== '') {
        filtered = filtered.filter(job => {
          const cellValue = job[columnKey];
          if (cellValue === null || cellValue === undefined) return false;
          return String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
        });
      }
    });

    setFilteredJobs(filtered);
  }, [jobs, columnFilters]);

  // Apply filters whenever jobs or columnFilters change
  useEffect(() => {
    applyFilters();
  }, [jobs, columnFilters, applyFilters]);

  const loadStores = async () => {
    try {
      const storesData = await crmDataService.getStores();
      const sortedStores = storesData.sort((a, b) =>
        (a.store_name || '').localeCompare(b.store_name || '')
      );
      setStores(sortedStores);
    } catch (err) {
      console.error('Failed to load stores:', err);
    }
  };

  const loadStatuses = async () => {
    try {
      const response = await jobService.getJobStatuses();
      if (response.success && Array.isArray(response.data)) {
        const statusOptions = response.data.map(s => s?.ta_job_status);
        setJobStatusOptions(statusOptions);
      }
    } catch (err) {
      console.error('Failed to load job statuses:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await jobService.getAllJobs();
      setJobs(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading jobs:', err);
      setError('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleColumnFilterChange = (columnKey, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnKey]: value
    }));
  };

  const clearAllFilters = () => {
    setColumnFilters({});
  };

  // Handle inline cell update
  const handleCellUpdate = async (jobId, field, value) => {
    try {
      // Prepare update data
      const updateData = { [field]: value };

      // If recruiter is being assigned, automatically set assigned_date to now
      if (field === 'recruiter' && value && value.trim() !== '') {
        const currentJob = jobs.find(j => j.id === jobId);
        // Only set assigned_date if it's not already set
        if (!currentJob?.assigned_date) {
          const now = new Date().toISOString(); // Full timestamp
          updateData.assigned_date = now;
        }
      }

      // Optimistically update UI
      setJobs(prev =>
        prev.map(j =>
          j.id === jobId ? { ...j, ...updateData } : j
        )
      );

      // Send update to server
      await jobService.updateJob(jobId, updateData);
    } catch (err) {
      console.error('Error updating job:', err);
      setError('Failed to update job. Please try again.');
      // Reload data to revert optimistic update
      loadData();
    }
  };

  // Get location name from store ID
  const getLocationName = (locationId) => {
    if (!locationId) return '';
    const store = stores.find(s => s.id === parseInt(locationId));
    return store?.store_name || locationId;
  };

  // Format date in friendly format: "10/21/2025 at 11:21am"
  const formatFriendlyDate = (dateString) => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);

      // Format date part: MM/DD/YYYY
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      // Format time part: HH:MMam/pm
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12 || 12; // Convert to 12-hour format

      return `${month}/${day}/${year} at ${hours}:${minutes}${ampm}`;
    } catch (e) {
      return dateString;
    }
  };

  // Handle opening form for new job
  const handleAddNew = () => {
    setEditingJob(null);
    setShowForm(true);
  };

  // Handle opening form for editing
  const handleEdit = (job) => {
    setEditingJob(job);
    setShowForm(true);
  };

  // Handle form save
  const handleFormSave = async (jobData) => {
    try {
      if (editingJob) {
        await jobService.updateJob(editingJob.id, jobData);
      } else {
        await jobService.createJob(jobData);
      }
      setShowForm(false);
      setEditingJob(null);
      loadData();
    } catch (err) {
      console.error('Error saving job:', err);
      throw err;
    }
  };

  // Handle delete
  const handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      await jobService.deleteJob(jobId);
      loadData();
    } catch (err) {
      console.error('Error deleting job:', err);
      setError('Failed to delete job. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="job-tracker-loading">
        <div className="loading-spinner"></div>
        <p>Loading Job Tracker...</p>
      </div>
    );
  }

  return (
    <div className="job-tracker">
      <div className="tracker-header">
        <h1>Job Tracker</h1>
        <div className="tracker-actions">
          <button className="btn-primary" onClick={handleAddNew}>
            + Add Job
          </button>
          <button className="btn-secondary" onClick={loadData}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Excel-like Grid */}
      <div className="tracker-grid-container">
        <table className="tracker-grid">
          <thead>
            <tr>
              <th className="action-column" rowSpan="2">
                Actions
                {Object.keys(columnFilters).length > 0 && (
                  <button
                    className="clear-filters-btn"
                    onClick={clearAllFilters}
                    title="Clear all filters"
                  >
                    ✕
                  </button>
                )}
              </th>
              {JOB_COLUMNS.map(col => (
                <th key={col.key} style={{ minWidth: col.width }}>
                  {col.label}
                  {col.required && <span className="required">*</span>}
                </th>
              ))}
            </tr>
            <tr className="filter-row">
              {JOB_COLUMNS.map(col => (
                <th key={`filter-${col.key}`} className="filter-cell">
                  <input
                    type="text"
                    className="column-filter-input"
                    placeholder={`Filter ${col.label}...`}
                    value={columnFilters[col.key] || ''}
                    onChange={(e) => handleColumnFilterChange(col.key, e.target.value)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredJobs.length === 0 ? (
              <tr>
                <td colSpan={JOB_COLUMNS.length + 1} className="no-data">
                  {jobs.length === 0
                    ? "No jobs found. Click '+ Add Job' to get started."
                    : "No jobs match your filters."}
                </td>
              </tr>
            ) : (
              filteredJobs.map(job => (
                <tr key={job.id}>
                  <td className="action-column">
                    <button
                      className="btn-icon"
                      onClick={() => handleEdit(job)}
                      title="Edit in form"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleDelete(job.id)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </td>
                  {JOB_COLUMNS.map(col => {
                    // Special handling for location field
                    if (col.key === 'location') {
                      const locationOptions = stores.map(store => ({
                        value: store.id,
                        label: store.store_name
                      }));
                      return (
                        <td key={col.key}>
                          <EditableCell
                            value={job[col.key]}
                            displayValue={getLocationName(job[col.key])}
                            type="select"
                            options={locationOptions}
                            placeholder="Select Location"
                            onSave={(value) => handleCellUpdate(job.id, col.key, value)}
                          />
                        </td>
                      );
                    }

                    // Special handling for assigned_date field - friendly format
                    if (col.key === 'assigned_date') {
                      return (
                        <td key={col.key}>
                          <div className="readonly-cell">
                            {formatFriendlyDate(job[col.key])}
                          </div>
                        </td>
                      );
                    }

                    // Special handling for calculated columns
                    if (col.type === 'calculated' && col.calculate) {
                      return (
                        <td key={col.key}>
                          <div className="readonly-cell calculated-cell">
                            {col.calculate(job)}
                          </div>
                        </td>
                      );
                    }

                    return (
                      <td key={col.key}>
                        {col.readOnly ? (
                          <div className="readonly-cell">
                            {col.type === 'datetime' && job[col.key]
                              ? new Date(job[col.key]).toLocaleString()
                              : job[col.key] || '-'}
                          </div>
                        ) : (
                          <EditableCell
                            value={job[col.key]}
                            type={col.type}
                            options={col.key === 'job_status'
                              ? jobstatusoptions :col.options}
                            placeholder={col.label}
                            onSave={(value) => handleCellUpdate(job.id, col.key, value)}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Job Form Modal */}
      {showForm && (
        <JobForm
          job={editingJob}
          onSave={handleFormSave}
          onClose={() => {
            setShowForm(false);
            setEditingJob(null);
          }}
        />
      )}
    </div>
  );
}

export default JobTracker;
